import { z } from 'zod';

import { cuidLike, nonEmptyString } from '../zod-common';

export const SELECTION_STATUSES = [
  'PENDING_BIDDER_ACCEPT',
  'AWAITING_REQUESTER_PAYMENT',
  'PAYMENT_COMPLETED',
  'PAYMENT_FAILED',
  'EXPIRED',
  'WITHDRAWN_BY_REQUESTER',
  'BIDDER_DECLINED',
] as const;
export type SelectionStatusValue = (typeof SELECTION_STATUSES)[number];

export const DISCREPANCY_DECISIONS = ['PENDING', 'ACCEPTED', 'REFUSED'] as const;
export type DiscrepancyDecisionValue = (typeof DISCREPANCY_DECISIONS)[number];

export const PROMO_CODE_MAX_LENGTH = 32;

export const preselectBidSchema = z.object({
  offerId: cuidLike,
  bidId: cuidLike,
  promoCode: z.string().trim().min(3).max(PROMO_CODE_MAX_LENGTH).optional(),
});
export type PreselectBidInput = z.infer<typeof preselectBidSchema>;

export const applySelectionPromoCodeSchema = z.object({
  selectionId: cuidLike,
  promoCode: z.string().trim().min(3).max(PROMO_CODE_MAX_LENGTH),
});
export type ApplySelectionPromoCodeInput = z.infer<typeof applySelectionPromoCodeSchema>;

export const decideDiscrepancySchema = z.object({
  discrepancyId: cuidLike,
  decision: z.enum(['ACCEPTED', 'REFUSED']),
  refusalComment: z.string().trim().max(500).optional(),
});
export type DecideDiscrepancyInput = z.infer<typeof decideDiscrepancySchema>;

export const bulkDecideDiscrepanciesSchema = z.object({
  selectionId: cuidLike,
  decisions: z
    .array(
      z.object({
        discrepancyId: cuidLike,
        decision: z.enum(['ACCEPTED', 'REFUSED']),
        refusalComment: z.string().trim().max(500).optional(),
      }),
    )
    .min(1, 'AT_LEAST_ONE_DECISION')
    .max(200, 'TOO_MANY_DECISIONS'),
});
export type BulkDecideDiscrepanciesInput = z.infer<typeof bulkDecideDiscrepanciesSchema>;

export const submitSelectionToBidderSchema = z.object({
  selectionId: cuidLike,
});
export type SubmitSelectionToBidderInput = z.infer<typeof submitSelectionToBidderSchema>;

export const reintegratedLinePriceSchema = z.object({
  quoteLineId: cuidLike,
  quantity: z.number().nonnegative().max(100_000).nullable().optional(),
  unitPrice: z.number().nonnegative().max(1_000_000_000).nullable().optional(),
  adjustment: z.number().min(-1_000_000_000).max(1_000_000_000).nullable().optional(),
});
export type ReintegratedLinePriceInput = z.infer<typeof reintegratedLinePriceSchema>;

export const bidderAcceptSelectionSchema = z.object({
  selectionId: cuidLike,
  linePrices: z.array(reintegratedLinePriceSchema).max(200).optional(),
});
export type BidderAcceptSelectionInput = z.infer<typeof bidderAcceptSelectionSchema>;

export const bidderPaymentRequirementSchema = z.object({
  selectionId: cuidLike,
});
export type BidderPaymentRequirementInput = z.infer<typeof bidderPaymentRequirementSchema>;

export const bidderStartPaymentSetupSchema = z.object({
  selectionId: cuidLike,
});
export type BidderStartPaymentSetupInput = z.infer<typeof bidderStartPaymentSetupSchema>;

export const bidderDeclineSelectionSchema = z.object({
  selectionId: cuidLike,
  reason: nonEmptyString.max(2_000, 'Max 2000 caractères'),
});
export type BidderDeclineSelectionInput = z.infer<typeof bidderDeclineSelectionSchema>;

export const withdrawBidderSchema = z.object({
  selectionId: cuidLike,
  reason: nonEmptyString.max(2_000, 'Max 2000 caractères'),
});
export type WithdrawBidderInput = z.infer<typeof withdrawBidderSchema>;

export const retryPaymentSchema = z.object({
  selectionId: cuidLike,
});
export type RetryPaymentInput = z.infer<typeof retryPaymentSchema>;

export const extendBidderDeadlineSchema = z.object({
  selectionId: cuidLike,
});
export type ExtendBidderDeadlineInput = z.infer<typeof extendBidderDeadlineSchema>;

export const selectionByOfferIdSchema = z.object({
  offerId: cuidLike,
});
export type SelectionByOfferIdInput = z.infer<typeof selectionByOfferIdSchema>;

export const selectionByIdSchema = z.object({
  selectionId: cuidLike,
});
export type SelectionByIdInput = z.infer<typeof selectionByIdSchema>;
